import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { Box, Button } from "@bca/ui-library";
import { position, PositionProps } from "styled-system";
import styled from "styled-components";

import { Image } from "../vehicleDetails/vehicle-details-dto.type";
import ImageWithZoom from "./ImageWithZoom";
import ImageGallery from "./ImageGallery";
import DoubleTapToZoom from "./DoubleTapToZoom";
import pushToDigitalData from "../analytics/pushToDigitalData";
import getFilteredImagesByType from "../helpers/getFilteredImagesByType";
import { shallowEqual, useSelector } from "react-redux";
import { GET_USER_STATE } from "../redux/slices/userSlice";
import { GET_VENDOR_IMPERSONATION_STATE } from "../redux/slices/impersonationSlice";
import { StoreType } from "../redux/StoreType";
interface ModalZoomImageProps extends PositionProps {
  images: Image[];
  imagesWithDuplicates: any;
  activeImageIndex: number;
  activeImageIndexRef: any;
  setShowImageZoomModal: (value: boolean) => void;
  setActiveImageIndex: () => void;
}

const ModalContainer = styled(Box)<PositionProps>`
  ${position}
`;

const CloseIconButton = styled(Button)<PositionProps>`
  ${position}
  &>div {
    filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.7));
  }
`;

const pushCloseButtonToAnalytics = (
  images: Image[],
  activeImageIndex: number,
  isVendorAdmin: boolean,
  isVendorImpersonationSet: boolean
) => {
  images.splice(0, 2).splice(images.length - 2, 2); // removes first 2 and last 2 items
  const wantedImage = images[activeImageIndex - 1];
  const wantedImageType = wantedImage?.imageType.toLowerCase();

  const filteredImages = getFilteredImagesByType(
    images,
    wantedImageType === "damage" ? "damage" : "beauty"
  );
  const indexOfWantedImagePerCategory = filteredImages.indexOf(wantedImage) + 1;

  if ((isVendorAdmin && isVendorImpersonationSet) || isVendorAdmin === false) {
    pushToDigitalData({
      event: {
        name: "click",
        category: "button",
        label: `Image|${activeImageIndex}`,
        action: "close",
        section: "gallery",
      },
      product: {
        image: {
          imageType:
            wantedImage?.imageType.toLowerCase() === "damage"
              ? "damage"
              : "beauty",
          imageNum: indexOfWantedImagePerCategory,
          imagePlacementNum: activeImageIndex,
        },
      },
    });
  }
};

const ImageZoomModal: React.FunctionComponent<ModalZoomImageProps> = ({
  images,
  imagesWithDuplicates,
  setShowImageZoomModal,
  activeImageIndex,
  setActiveImageIndex,
  activeImageIndexRef,
}) => {
  const isMobileDevice =
    typeof window !== "undefined" ? window.innerWidth < 768 : false; // 768 - anything less than a tablet

  const imagesWithFullSize = (images) =>
    images.filter(({ fullSize }) => fullSize);

  const handleCloseModal = (event) => {
    if (event.keyCode === 27) {
      setShowImageZoomModal(false);
    }
  };

  const {
    user: { isVendorAdmin },
  }: StoreType = useSelector(GET_USER_STATE, shallowEqual);
  const {
    impersonation: { isVendorImpersonationSet },
  }: StoreType = useSelector(GET_VENDOR_IMPERSONATION_STATE, shallowEqual);

  useEffect(() => {
    document.addEventListener("keydown", handleCloseModal, false);
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleCloseModal, false);
      document.documentElement.style.overflow = "unset";
    };
  }, []);

  return ReactDOM.createPortal(
    <>
      <ModalContainer
        alignItems="center"
        display="flex"
        height="auto"
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        left={0}
        zIndex={999}
      >
        {isMobileDevice && <DoubleTapToZoom />}
        <CloseIconButton
          aria-label="Close Modal"
          position="absolute"
          right={[20, 40, 40]}
          top={[20, 40, 40]}
          icon="times"
          iconWeight="light"
          iconColor="#fff"
          zIndex={1}
          padding={0}
          onClick={() => {
            setShowImageZoomModal(false);
            pushCloseButtonToAnalytics(
              imagesWithDuplicates,
              activeImageIndex - 1,
              isVendorAdmin,
              isVendorImpersonationSet
            );
          }}
          variant="tertiary"
          width={[24, 40, 40]}
          height={[24, 40, 40]}
        ></CloseIconButton>
        <ImageGallery
          images={images}
          slideSpacing={0}
          activeImageIndex={activeImageIndex}
          setActiveImageIndex={setActiveImageIndex}
          activeImageIndexRef={activeImageIndexRef}
          hasImageFullWidth
          disableSwipe
        >
          {imagesWithDuplicates.map((image, index) => {
            const [{ imageURI }] = imagesWithFullSize(image?.imageSizes);
            return (
              <Box
                width="100%"
                height="100%"
                key={imageURI + "zoomImage" + index}
                aria-label="Selected Image"
              >
                <ImageWithZoom imageSrc={imageURI} largeImageSrc={imageURI} />
              </Box>
            );
          })}
        </ImageGallery>
      </ModalContainer>
    </>,
    document.getElementById("modal-root")
  );
};

export default ImageZoomModal;
